import { FC, useMemo, useReducer } from 'react';
import { Button, Stack, Tooltip, Typography, styled } from '@mui/material';
import GradientBorderBox from '@/components/Focus/common/GradientBorderBox';
import ScheduleSelections from '@/components/Focus/AvailsSchedule/NewSchedule/ScheduleSelections';
import { FocusTheme } from '@/components/Focus/configs/theme';
import { useAppSelector } from '@/redux/hooks';
import { selectFocusNewScheduleSlice } from '@/redux/selectors/focusNewScheduleSelectors';
import ScheduleSelectedLanguage from '../../ScheduleDetails/ScheduleSelectedLanguage';
import ScheduleSelectedTerritories from '../../ScheduleDetails/ScheduleSelectedTerritories';
import IncrementalLaunchStep from './IncrementalLaunchStep';
import IncrementalLaunchTimingSection, { IncrementalLaunchTimingValue } from './IncrementalLaunchTimingSection/IncrementalLaunchTimingSection';
import {
  getFormattedFirstOccurrence,
  getReleaseTimingStepCompleted,
  getRightsStepState,
  getUserTimezoneAbbreviation,
  hasRightsChangesForTab
} from '@/components/Focus/utils/incrementalLaunchWorkspace';
import { getPreviousLaunchReleaseTime } from '@/components/Focus/utils/incrementalLaunchList';
import { ScheduleAvailType, TitleEntity } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import LaunchApplyIncrementSection, { IncrementalSelectionState } from '../LaunchApplyIncrementSection';

export type IncrementalLaunchRightsSelectionState = IncrementalSelectionState;

export type IncrementalLaunchRightsDraftByTab = Record<ScheduleAvailType, IncrementalLaunchRightsSelectionState>;

export type IncrementalLaunchSelectedVersions = {
  add: (TitleEntity | TitleEntityV2)['id'][];
  remove: (TitleEntity | TitleEntityV2)['id'][];
};

export type IncrementalLaunchWorkspaceDraft = {
  incrementalLaunchTimingValue: IncrementalLaunchTimingValue;
  incrementalLaunchRightsDraftByTab: IncrementalLaunchRightsDraftByTab;
  incrementalLaunchVersions: IncrementalLaunchSelectedVersions;
};

type IncrementalLaunchWorkspaceState = {
  selectedRightsTab: ScheduleAvailType;
  incrementalLaunchTimingValue: IncrementalLaunchTimingValue;
  incrementalLaunchRightsDraftByTab: IncrementalLaunchRightsDraftByTab;
  incrementalLaunchVersions: IncrementalLaunchSelectedVersions;
};

type IncrementalLaunchWorkspaceAction =
  | {
      type: 'set_selected_rights_tab';
      payload: ScheduleAvailType;
    }
  | {
      type: 'set_incremental_launch_timing_value';
      payload: IncrementalLaunchTimingValue;
    }
  | {
      type: 'set_incremental_launch_rights_draft_for_tab';
      payload: {
        tab: ScheduleAvailType;
        value: IncrementalLaunchRightsSelectionState;
      };
    }
  | {
      type: 'set_incremental_launch_versions';
      payload: IncrementalLaunchSelectedVersions;
    }
  | {
      type: 'hydrate_incremental_launch_workspace';
      payload: IncrementalLaunchWorkspaceState;
    }
  | {
      type: 'reset_incremental_launch_workspace';
      payload: IncrementalLaunchWorkspaceState;
    };

type IncrementalLaunchWorkspaceProps = {
  onCancel: () => void;
  onSave: (draft: IncrementalLaunchWorkspaceDraft) => void;
  initialReleaseDateIso: string;
  isEditing?: boolean;
  initialDraft?: IncrementalLaunchWorkspaceDraft;
  initialId?: string;
};

const createIncrementalLaunchWorkspaceState = ({
  previousLaunchReleaseTime,
  initialDraft
}: {
  previousLaunchReleaseTime: Date;
  selectedTerritories?: string[];
  selectedDubs?: string[];
  selectedSubs?: string[];
  selectedVersions?: (TitleEntity | TitleEntityV2)['id'][];
  initialDraft?: IncrementalLaunchWorkspaceDraft;
}): IncrementalLaunchWorkspaceState => {
  const timezone = getUserTimezoneAbbreviation();

  return {
    selectedRightsTab: 'territories',
    incrementalLaunchTimingValue: initialDraft?.incrementalLaunchTimingValue ?? {
      offsetUnit: 'Days',
      offsetValue: 0,
      releaseTime: new Date(previousLaunchReleaseTime),
      timezone
    },
    incrementalLaunchRightsDraftByTab: initialDraft?.incrementalLaunchRightsDraftByTab ?? {
      territories: {
        add: [],
        remove: []
      },
      audio: {
        add: [],
        remove: []
      },
      timedtext: {
        add: [],
        remove: []
      }
    },
    incrementalLaunchVersions: initialDraft?.incrementalLaunchVersions ?? {
      add: [],
      remove: []
    }
  };
};

const incrementalLaunchWorkspaceReducer = (
  state: IncrementalLaunchWorkspaceState,
  action: IncrementalLaunchWorkspaceAction
): IncrementalLaunchWorkspaceState => {
  switch (action.type) {
    case 'set_selected_rights_tab':
      return {
        ...state,
        selectedRightsTab: action.payload
      };

    case 'set_incremental_launch_timing_value':
      return {
        ...state,
        incrementalLaunchTimingValue: action.payload
      };

    case 'set_incremental_launch_rights_draft_for_tab':
      return {
        ...state,
        incrementalLaunchRightsDraftByTab: {
          ...state.incrementalLaunchRightsDraftByTab,
          [action.payload.tab]: action.payload.value
        }
      };

    case 'set_incremental_launch_versions': {
      return {
        ...state,
        incrementalLaunchVersions: action.payload
      };
    }

    case 'hydrate_incremental_launch_workspace':
    case 'reset_incremental_launch_workspace':
      return action.payload;

    default:
      return state;
  }
};

const IncrementalLaunchWorkspace: FC<IncrementalLaunchWorkspaceProps> = ({
  onCancel,
  onSave,
  initialReleaseDateIso,
  isEditing,
  initialDraft,
  initialId
}) => {
  const {
    rightsPossible,
    selectedDubs: originalSelectedDubs,
    selectedSubs: originalSelectedSubs,
    selectedTerritories: originalSelectedTerritories,
    selectedVersions: originalSelectedVersions,
    launchList
  } = useAppSelector(selectFocusNewScheduleSlice);
  const userTimezone = getUserTimezoneAbbreviation();

  const launch = launchList.find((l) => l.id === initialId);
  const isInitialLaunch = !launch || launchList.findIndex((l) => l.id === initialId) === 0;

  const selectedDubs = launch && !isInitialLaunch ? launch.incrementalLaunchRightsDraftByTab.audio.add : originalSelectedDubs;
  const selectedSubs = launch && !isInitialLaunch ? launch.incrementalLaunchRightsDraftByTab.timedtext.add : originalSelectedSubs;
  const selectedTerritories = launch && !isInitialLaunch ? launch.incrementalLaunchRightsDraftByTab.territories.add : originalSelectedTerritories;
  const selectedVersions = originalSelectedVersions;

  const previousLaunchReleaseTime = useMemo(
    () =>
      getPreviousLaunchReleaseTime({
        launchList,
        launchId: initialId,
        initialReleaseDateIso
      }),
    [launchList, initialId, initialReleaseDateIso]
  );

  const [incrementalLaunchWorkspaceState, dispatchIncrementalLaunchWorkspaceAction] = useReducer(
    incrementalLaunchWorkspaceReducer,
    createIncrementalLaunchWorkspaceState({
      previousLaunchReleaseTime,
      selectedTerritories,
      selectedDubs,
      selectedSubs,
      selectedVersions,
      initialDraft
    })
  );

  const { selectedRightsTab, incrementalLaunchTimingValue, incrementalLaunchRightsDraftByTab, incrementalLaunchVersions } =
    incrementalLaunchWorkspaceState;

  // Temporary: the selection section currently stores incremental launch selections
  // in `remove` because those rights are removed from the first-time launch.
  const selectedTerritoriesForPreview = incrementalLaunchRightsDraftByTab.territories.remove;
  const selectedAudioForPreview = incrementalLaunchRightsDraftByTab.audio.remove;
  const selectedTimedTextForPreview = incrementalLaunchRightsDraftByTab.timedtext.remove;

  const hasRightsChanges = Object.values(incrementalLaunchRightsDraftByTab).some((tab) => tab.add?.length > 0 || tab.remove?.length > 0);
  const hasVersionsChanges = incrementalLaunchVersions.add.length > 0 || incrementalLaunchVersions.remove.length > 0;
  const hasWorkspaceChanges = hasRightsChanges || hasVersionsChanges;
  const isReleaseTimingStepCompleted = getReleaseTimingStepCompleted({
    incrementalLaunchTimingValue,
    previousLaunchReleaseTime,
    isEditing
  });
  const isRemovingAllTerritories = selectedTerritoriesForPreview.length === originalSelectedTerritories.length;

  const saveDisabledReason = isRemovingAllTerritories
    ? 'Cannot remove all territories from previous launch.'
    : !hasWorkspaceChanges
      ? 'Need at least one territory or rights change to save incremental launch.'
      : !isReleaseTimingStepCompleted
        ? 'Need to add release offset time to save incremental launch.'
        : '';

  const canSaveWorkspace = !saveDisabledReason;

  const formattedFirstOccurrence = getFormattedFirstOccurrence({
    previousLaunchReleaseTime,
    timing: incrementalLaunchTimingValue
  });

  const dubRights = useMemo(() => {
    if (!rightsPossible?.territories) return [];

    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.audio ?? [])
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const subRights = useMemo(() => {
    if (!rightsPossible?.territories) return [];

    return Array.from(
      new Set(
        Object.values(rightsPossible.territories)
          .flatMap((territory) => territory.timedtext ?? [])
          .map((item) => item.language)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [rightsPossible]);

  const regionRights = useMemo(() => {
    if (!rightsPossible?.territories) return [];

    return Object.keys(rightsPossible.territories).map((territory) => territory.toUpperCase());
  }, [rightsPossible]);

  const rightsOptions = selectedRightsTab === 'audio' ? dubRights : selectedRightsTab === 'timedtext' ? subRights : regionRights;
  const incrementalLaunchRightsSelectionValue = incrementalLaunchRightsDraftByTab[selectedRightsTab];

  const handleSelectRightsTab = (tab: ScheduleAvailType) => {
    dispatchIncrementalLaunchWorkspaceAction({
      type: 'set_selected_rights_tab',
      payload: tab
    });
  };

  const handleChangeIncrementalLaunchTimingValue = (nextIncrementalLaunchTimingValue: IncrementalLaunchTimingValue) => {
    dispatchIncrementalLaunchWorkspaceAction({
      type: 'set_incremental_launch_timing_value',
      payload: nextIncrementalLaunchTimingValue
    });
  };

  const handleChangeIncrementalLaunchRightsDraftForSelectedTab = (
    nextIncrementalLaunchRightsSelectionValue: IncrementalLaunchRightsSelectionState
  ) => {
    dispatchIncrementalLaunchWorkspaceAction({
      type: 'set_incremental_launch_rights_draft_for_tab',
      payload: {
        tab: selectedRightsTab,
        value: nextIncrementalLaunchRightsSelectionValue
      }
    });
  };

  const handleSaveWorkspace = () => {
    onSave({
      incrementalLaunchTimingValue,
      incrementalLaunchRightsDraftByTab,
      incrementalLaunchVersions
    });
  };

  return (
    <MainLayout>
      <LeftContainer>
        <HeaderBlock>
          <Typography variant="h4" sx={{ color: 'neutral.10', fontWeight: 'bold' }}>
            {isEditing ? 'Edit Incremental Launch' : 'Add Incremental Launch'}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 16 }}>Configure availability updates for selected titles.</Typography>
        </HeaderBlock>
        <WorkspaceGrid>
          <ReleaseTimingStepCell>
            <Stack justifyContent="center" height="100%" mr={0.5}>
              <IncrementalLaunchStep step={1} label="Release Timing" state="active" isCompleted={isReleaseTimingStepCompleted} icon="timer_outline" />
            </Stack>
          </ReleaseTimingStepCell>
          <ReleaseTimingContentCell>
            <Stack direction="row" flexWrap="wrap" alignItems="center" useFlexGap>
              <Stack spacing={0.5} mr={3}>
                <Typography sx={{ color: 'neutral.10' }}>Release Offset</Typography>
                <Typography variant="caption" sx={{ color: 'neutral.30', fontWeight: 'normal' }}>
                  Time between previously scheduled launches.
                </Typography>
              </Stack>
              <Stack flex="1" width="100%">
                <IncrementalLaunchTimingSection
                  baseReleaseTime={previousLaunchReleaseTime}
                  offsetOptions={['Hours', 'Days', 'Weeks']}
                  onChange={handleChangeIncrementalLaunchTimingValue}
                  timezoneOptions={[userTimezone]}
                  value={incrementalLaunchTimingValue}
                />
              </Stack>
            </Stack>
          </ReleaseTimingContentCell>
          <BottomLeftRail>
            <Stack spacing={2}>
              <IncrementalLaunchStep
                step={2}
                label="Territories"
                state={getRightsStepState({
                  selectedRightsTab,
                  tab: 'territories'
                })}
                isCompleted={hasRightsChangesForTab(incrementalLaunchRightsDraftByTab, 'territories')}
                onClick={() => handleSelectRightsTab('territories')}
                icon="map"
              />
              <IncrementalLaunchStep
                step={3}
                label="Audio"
                state={getRightsStepState({
                  selectedRightsTab,
                  tab: 'audio'
                })}
                isCompleted={hasRightsChangesForTab(incrementalLaunchRightsDraftByTab, 'audio')}
                onClick={() => handleSelectRightsTab('audio')}
                icon="music"
              />
              <IncrementalLaunchStep
                step={4}
                label="Timed Text"
                state={getRightsStepState({
                  selectedRightsTab,
                  tab: 'timedtext'
                })}
                isCompleted={hasRightsChangesForTab(incrementalLaunchRightsDraftByTab, 'timedtext')}
                onClick={() => handleSelectRightsTab('timedtext')}
                icon="subtitles"
              />
            </Stack>
          </BottomLeftRail>
          <BottomMain>
            <LaunchApplyIncrementSection
              tab={selectedRightsTab}
              value={incrementalLaunchRightsSelectionValue}
              originalRights={selectedRightsTab === 'audio' ? selectedDubs : selectedRightsTab === 'timedtext' ? selectedSubs : selectedTerritories}
              onChange={handleChangeIncrementalLaunchRightsDraftForSelectedTab}
              rightsOptions={rightsOptions}
            />
          </BottomMain>
        </WorkspaceGrid>
      </LeftContainer>
      <RightContainer>
        <GradientBorderBox>
          <PreviewContainer>
            <Stack spacing={1}>
              <PreviewTitle variant="h4">Incremental Launch Preview</PreviewTitle>
              <PreviewSubtitle>Review the scheduled updates included in this incremental launch.</PreviewSubtitle>
              <Typography my={1}>
                <FirstOccurrenceLabel>First occurrence: </FirstOccurrenceLabel>
                <FirstOccurrenceValue>{formattedFirstOccurrence}</FirstOccurrenceValue>
              </Typography>
            </Stack>
            <PreviewContent>
              <ScheduleSelections readOnly>
                <Stack spacing={4}>
                  {selectedTerritoriesForPreview.length > 0 ? (
                    <ScheduleSelectedTerritories readOnly selectedTerritories={selectedTerritoriesForPreview} />
                  ) : (
                    <NoChangesPreviewRow>
                      <Typography>Territories</Typography>
                      <NoChangesValue variant="caption">No changes</NoChangesValue>
                    </NoChangesPreviewRow>
                  )}
                  {selectedAudioForPreview.length > 0 ? (
                    <ScheduleSelectedLanguage readOnly type="audio" selectedDubs={selectedAudioForPreview} />
                  ) : (
                    <NoChangesPreviewRow>
                      <Typography>Audio</Typography>
                      <NoChangesValue variant="caption">No changes</NoChangesValue>
                    </NoChangesPreviewRow>
                  )}
                  {selectedTimedTextForPreview.length > 0 ? (
                    <ScheduleSelectedLanguage readOnly type="subtitle" selectedSubs={selectedTimedTextForPreview} />
                  ) : (
                    <NoChangesPreviewRow>
                      <Typography>Timed Text</Typography>
                      <NoChangesValue variant="caption">No changes</NoChangesValue>
                    </NoChangesPreviewRow>
                  )}
                </Stack>
              </ScheduleSelections>
            </PreviewContent>
            <PreviewActions>
              <Tooltip title={saveDisabledReason} disableHoverListener={canSaveWorkspace}>
                <span>
                  <Button variant="contained" onClick={handleSaveWorkspace} disabled={!canSaveWorkspace} sx={{ fontWeight: 'normal' }}>
                    Save
                  </Button>
                </span>
              </Tooltip>
              <Button variant="text" onClick={onCancel} sx={{ color: 'neutral.30' }}>
                Cancel
              </Button>
            </PreviewActions>
          </PreviewContainer>
        </GradientBorderBox>
      </RightContainer>
    </MainLayout>
  );
};

export default IncrementalLaunchWorkspace;

const MainLayout = styled('section')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const LeftContainer = styled('main')`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  align-items: stretch;
`;

const RightContainer = styled('aside')`
  display: flex;
  flex: 0 0 clamp(320px, 30%, 480px);
`;

const WorkspaceGrid = styled('section')`
  display: grid;
  grid-template-columns: minmax(0, auto) minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  overflow: hidden;
  border-radius: 0 0 ${({ theme }) => theme.shape.borderRadius}px ${({ theme }) => theme.shape.borderRadius}px;
  height: 100%;
`;

const HeaderBlock = styled('header')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-radius: ${({ theme }) => `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`};
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const ReleaseTimingStepCell = styled('div')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-bottom: ${({ theme }) => `${theme.spacing(0.5)} solid ${(theme as FocusTheme).palette.neutral[90]}`};
  padding: ${({ theme }) => theme.spacing(2, 0, 2, 2)};
`;

const ReleaseTimingContentCell = styled('div')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-bottom: ${({ theme }) => `${theme.spacing(0.5)} solid ${(theme as FocusTheme).palette.neutral[90]}`};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const BottomLeftRail = styled('nav')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-right: ${({ theme }) => `${theme.spacing(0.5)} solid ${(theme as FocusTheme).palette.neutral[90]}`};
  padding: ${({ theme }) => theme.spacing(2, 0, 0, 2)};
`;

const BottomMain = styled('div')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  padding: ${({ theme }) => theme.spacing(2)};
`;

const PreviewContainer = styled('section')`
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[70]};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  padding: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const PreviewTitle = styled(Typography)`
  color: ${({ theme }) => (theme as FocusTheme).palette.secondary.main};
  font-weight: 700;
`;

const PreviewSubtitle = styled(Typography)`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[20]};
  font-size: 16px;
`;

const PreviewContent = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing(2)};

  & > div {
    width: 100%;
  }
`;

const FirstOccurrenceLabel = styled('span')`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
`;

const FirstOccurrenceValue = styled('span')`
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[10]};
  font-weight: ${({ theme }) => theme.typography.fontWeightMedium};
`;

const NoChangesPreviewRow = styled('div')`
  align-items: center;
  background-color: ${({ theme }) => (theme as FocusTheme).palette.neutral[80]};
  border: 1px solid ${({ theme }) => (theme as FocusTheme).palette.neutral[50]};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
`;

const NoChangesValue = styled(Typography)`
  font-weight: 500;
`;

const PreviewActions = styled('div')`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
  justify-content: flex-start;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;
